import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { StorageService } from '../admin services/storage.service';
import { FirestoreService } from '../admin services/firestore.service';
import { UserService } from '../user services/user.service';
import { IDBPhotoCacheService } from '../IDB/idbphoto-cache.service';
import { AnonymousUserService } from '../user services/anonymous-user.service';
import { CompressionSettings, PhotoResult } from '../../interfaces/master-interfaces';




@Injectable({
  providedIn: 'root'
})



export class CameraService {




  // Default compression settings
  private readonly STORAGE_COMPRESSION: CompressionSettings = { maxDimension: 1200, quality: 0.8 };
  private readonly THUMBNAIL_COMPRESSION: CompressionSettings = { maxDimension: 150, quality: 0.6 };
  private readonly FALLBACK_COMPRESSION: CompressionSettings = { maxDimension: 800, quality: 0.5 };



  constructor(
    private _storageService: StorageService,
    private _firestoreService: FirestoreService,
    private _IDBphotoCacheService: IDBPhotoCacheService,
    private _anonymousUserService: AnonymousUserService
  ) { }




  async capturePhoto(providedLatitude: number, providedLongitude: number): Promise<PhotoResult | null> {
    const latitude = providedLatitude || 0;
    const longitude = providedLongitude || 0;

    try {
      // Validate dependencies
      if (!this._storageService) {
        throw new Error('Storage service not initialized');
      }

      console.log('[CameraService] capturePhoto() called with coords:', { latitude, longitude });

      // Check if Camera plugin is available
      if (typeof Camera === 'undefined' || !Camera.getPhoto) {
        console.warn('[CameraService] Camera plugin not available, using web fallback');
        return this.capturePhotoWeb(latitude, longitude);
      }

      // Open camera/gallery picker
      console.log('[CameraService] Opening camera picker...');
      const photo = await this.openCameraPicker();

      // If openCameraPicker returns null (browser detected), fall back to web
      if (!photo?.dataUrl) {
        console.log('[CameraService] Falling back to web photo capture');
        return this.capturePhotoWeb(latitude, longitude);
      }

      console.log('[CameraService] Photo captured, processing...');

      // Store original in IDB before compression
      await this.cacheOriginalPhoto(photo.dataUrl);

      // Compress for storage and thumbnail
      const storageImage = await this.compressImage(photo.dataUrl, this.STORAGE_COMPRESSION);
      const thumbnailImage = await this.compressImage(photo.dataUrl, this.THUMBNAIL_COMPRESSION);

      // Save to Firestore
      const docId = await this.savePhotoToFirestore(storageImage, latitude, longitude);
      if (!docId) {
        throw new Error('Failed to save photo to Firestore');
      }

      // Cache full-quality with permanent docId
      await this.cachePhotoWithDocId(docId, photo.dataUrl);

      console.log('[CameraService] Photo processing complete:', docId);

      return {
        photoUrl: photo.dataUrl,
        thumbnailUrl: thumbnailImage,
        docId: docId,
        latitude: latitude,
        longitude: longitude
      };

    } catch (error: any) {
      console.error('[CameraService] Photo capture failed:', error?.message || error);
      throw error;
    }
  }

  private async openCameraPicker(): Promise<{ dataUrl: string } | null> {
    try {
      // In browser, force web fallback
      if (!this.isNativeEnvironment()) {
        console.log('[CameraService] Browser detected, using web fallback');
        return null; // Will trigger web fallback in parent
      }

      console.log('[CameraService] Permissions OK, opening picker...');
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        promptLabelHeader: 'Add Photo',
        promptLabelCancel: 'Cancel',
        promptLabelPhoto: 'Choose from Gallery',
        promptLabelPicture: 'Take Picture',
        width: 1200,
        height: 1200,
        correctOrientation: true
      });

      if (!photo?.dataUrl) {
        return null;
      }

      return { dataUrl: photo.dataUrl };

    } catch (error: any) {
      console.error('[CameraService] openCameraPicker error:', error?.message || error);
      throw error;
    }
  }

  private isNativeEnvironment(): boolean {
    return (window as any).cordova || (window as any).capacitor;
  }

  private async cacheOriginalPhoto(dataUrl: string): Promise<void> {
    if (!this._IDBphotoCacheService) return;

    try {
      const tempId = `temp_${Date.now()}`;
      console.log('[CameraService] Caching original photo:', tempId);
      await this._IDBphotoCacheService.storeUrl(tempId, dataUrl);
    } catch (error) {
      console.warn('[CameraService] Failed to cache original photo:', error);
      // Non-fatal, continue
    }
  }

  private async cachePhotoWithDocId(docId: string, dataUrl: string): Promise<void> {
    if (!this._IDBphotoCacheService) return;

    try {
      console.log('[CameraService] Caching photo with docId:', docId);
      await this._IDBphotoCacheService.storeUrl(docId, dataUrl);

      const cached = await this._IDBphotoCacheService.getUrlFromCache(docId);
      if (cached) {
        console.log('[CameraService] Photo verified in cache');
      } else {
        console.warn('[CameraService] Photo not found in cache after storage');
      }
    } catch (error) {
      console.warn('[CameraService] Failed to cache photo with docId:', error);
      // Non-fatal, continue
    }
  }



  private capturePhotoWeb(latitude: number, longitude: number): Promise<PhotoResult | null> {
    return new Promise((resolve, reject) => {
      try {
        console.log('[CameraService] capturePhotoWeb() starting - showing source choice');

        // Create a styled modal
        const modalDiv = document.createElement('div');
        modalDiv.id = 'photo-source-modal-' + Date.now();
        modalDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        animation: fadeIn 0.3s ease-out;
      `;

        // Add keyframes for animations
        const styleSheet = document.createElement("style");
        styleSheet.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            transform: translateY(30px) scale(0.95);
            opacity: 0;
          }
          to { 
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .photo-source-btn {
          transition: all 0.2s ease;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }
        .photo-source-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.2);
        }
        .photo-source-btn:active {
          transform: scale(0.95);
        }
        .icon-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 24px 32px;
          border-radius: 16px;
          border: none;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          min-width: 160px;
          transition: all 0.2s ease;
        }
        .icon-btn .icon {
          font-size: 48px;
          line-height: 1;
        }
        .icon-btn .label {
          font-size: 14px;
          font-weight: 500;
        }
      `;
        document.head.appendChild(styleSheet);

        const contentDiv = document.createElement('div');
        contentDiv.style.cssText = `
        background: linear-gradient(145deg, #ffffff, #f8f9fa);
        border-radius: 24px;
        padding: 40px 32px 32px;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        max-width: 480px;
        width: 90%;
        animation: slideUp 0.4s ease-out;
        position: relative;
        border: 1px solid rgba(255, 255, 255, 0.1);
      `;

        // Add a subtle gradient header
        const headerDiv = document.createElement('div');
        headerDiv.style.cssText = `
        margin-bottom: 28px;
      `;

        const title = document.createElement('h3');
        title.textContent = 'Add Photo';
        title.style.cssText = `
        margin: 0 0 8px 0;
        font-size: 24px;
        font-weight: 700;
        color: #1a1a2e;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
        headerDiv.appendChild(title);

        const subtitle = document.createElement('p');
        subtitle.textContent = 'Choose how you want to add a photo';
        subtitle.style.cssText = `
        margin: 0;
        font-size: 14px;
        color: #6c757d;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
        headerDiv.appendChild(subtitle);

        contentDiv.appendChild(headerDiv);

        const buttonsDiv = document.createElement('div');
        buttonsDiv.style.cssText = `
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
      `;

        // Camera Button
        const cameraBtn = document.createElement('button');
        cameraBtn.className = 'icon-btn photo-source-btn';
        cameraBtn.style.cssText = `
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        flex: 1;
        min-width: 140px;
      `;
        cameraBtn.innerHTML = `
        <span class="icon">📷</span>
        <span class="label">Take Photo</span>
      `;

        // Gallery Button
        const galleryBtn = document.createElement('button');
        galleryBtn.className = 'icon-btn photo-source-btn';
        galleryBtn.style.cssText = `
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        color: white;
        flex: 1;
        min-width: 140px;
      `;
        galleryBtn.innerHTML = `
        <span class="icon">🖼️</span>
        <span class="label">Choose from Gallery</span>
      `;

        // Cancel Button
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'photo-source-btn';
        cancelBtn.style.cssText = `
        padding: 12px 32px;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        background: transparent;
        color: #6c757d;
        cursor: pointer;
        font-size: 15px;
        font-weight: 600;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin-top: 8px;
        width: 100%;
      `;

        // Hover effect for cancel
        cancelBtn.addEventListener('mouseenter', () => {
          cancelBtn.style.background = '#f8f9fa';
          cancelBtn.style.borderColor = '#dee2e6';
        });
        cancelBtn.addEventListener('mouseleave', () => {
          cancelBtn.style.background = 'transparent';
          cancelBtn.style.borderColor = '#e9ecef';
        });

        // Remove modal
        const closeModal = () => {
          try {
            if (modalDiv.parentElement) {
              modalDiv.style.opacity = '0';
              modalDiv.style.transition = 'opacity 0.3s ease';
              setTimeout(() => {
                if (modalDiv.parentElement) {
                  modalDiv.parentElement.removeChild(modalDiv);
                }
              }, 300);
            }
            if (styleSheet.parentElement) {
              styleSheet.parentElement.removeChild(styleSheet);
            }
          } catch (e) {
            console.warn('[CameraService] Error closing modal:', e);
          }
        };

        // Camera button
        cameraBtn.addEventListener('click', () => {
          console.log('[CameraService] User chose: Take Picture');
          closeModal();
          this.capturePhotoFromCamera(latitude, longitude).then(resolve).catch(reject);
        });

        // Gallery button
        galleryBtn.addEventListener('click', () => {
          console.log('[CameraService] User chose: Choose from Gallery');
          closeModal();
          this.capturePhotoFromGallery(latitude, longitude).then(resolve).catch(reject);
        });

        // Cancel button
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => {
          console.log('[CameraService] User cancelled source selection');
          closeModal();
          resolve(null);
        });

        buttonsDiv.appendChild(cameraBtn);
        buttonsDiv.appendChild(galleryBtn);
        contentDiv.appendChild(buttonsDiv);

        const cancelWrapper = document.createElement('div');
        cancelWrapper.style.cssText = `
        margin-top: 20px;
        width: 100%;
      `;
        cancelWrapper.appendChild(cancelBtn);
        contentDiv.appendChild(cancelWrapper);

        modalDiv.appendChild(contentDiv);
        document.body.appendChild(modalDiv);

        console.log('[CameraService] Photo source choice modal opened');

      } catch (setupError) {
        console.error('[CameraService] Setup error:', setupError);
        reject(setupError);
      }
    });
  }



  private showCameraPreview(
    stream: MediaStream,
    latitude: number,
    longitude: number,
    resolve: (value: PhotoResult | null) => void,
    reject: (error: any) => void
  ): void {
    // Create modal with video preview
    const modalDiv = document.createElement('div');
    modalDiv.id = 'camera-preview-modal-' + Date.now();
    modalDiv.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.4s ease-out;
  `;

    // Add keyframes if not already added
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideUp {
      from { transform: translateY(30px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .capture-btn {
      transition: all 0.2s ease;
      box-shadow: 0 4px 15px rgba(52, 199, 89, 0.4);
    }
    .capture-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 25px rgba(52, 199, 89, 0.6);
    }
    .capture-btn:active {
      transform: scale(0.92);
    }
    .cancel-btn {
      transition: all 0.2s ease;
    }
    .cancel-btn:hover {
      transform: scale(1.05);
    }
    .cancel-btn:active {
      transform: scale(0.92);
    }
  `;
    document.head.appendChild(styleSheet);

    const containerDiv = document.createElement('div');
    containerDiv.style.cssText = `
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    width: 100%;
    max-width: 600px;
    padding: 20px;
    animation: slideUp 0.5s ease-out;
  `;

    // Video container with rounded corners and shadow
    const videoWrapper = document.createElement('div');
    videoWrapper.style.cssText = `
    width: 100%;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    background: #000;
    position: relative;
  `;

    const video = document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.srcObject = stream;
    video.style.cssText = `
    width: 100%;
    height: auto;
    display: block;
    background: #000;
    aspect-ratio: 4/3;
    object-fit: cover;
  `;

    // Add a subtle gradient overlay for better button visibility
    const overlay = document.createElement('div');
    overlay.style.cssText = `
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(transparent, rgba(0,0,0,0.3));
    pointer-events: none;
  `;

    videoWrapper.appendChild(video);
    videoWrapper.appendChild(overlay);
    containerDiv.appendChild(videoWrapper);

    const buttonsDiv = document.createElement('div');
    buttonsDiv.style.cssText = `
    display: flex;
    gap: 16px;
    justify-content: center;
    width: 100%;
    padding: 0 10px;
  `;

    // Capture Button - Circle design
    const captureBtn = document.createElement('button');
    captureBtn.className = 'capture-btn';
    captureBtn.style.cssText = `
    padding: 18px 36px;
    border: none;
    border-radius: 60px;
    background: linear-gradient(135deg, #34C759, #28a745);
    color: white;
    cursor: pointer;
    font-size: 18px;
    font-weight: 700;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    flex: 2;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    box-shadow: 0 4px 20px rgba(52, 199, 89, 0.4);
  `;
    captureBtn.innerHTML = `
    <span style="font-size: 24px;">📸</span>
    Capture
  `;

    // Cancel Button - Circle design
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'cancel-btn';
    cancelBtn.style.cssText = `
    padding: 18px 28px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 60px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    flex: 1;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    transition: all 0.2s ease;
  `;
    cancelBtn.innerHTML = '✕ Cancel';

    // Create hidden canvas for capturing
    const canvas = document.createElement('canvas');
    canvas.style.display = 'none';

    const closeModal = () => {
      try {
        stream.getTracks().forEach(track => track.stop());
        if (modalDiv.parentElement) {
          modalDiv.style.opacity = '0';
          modalDiv.style.transition = 'opacity 0.3s ease';
          setTimeout(() => {
            if (modalDiv.parentElement) {
              modalDiv.parentElement.removeChild(modalDiv);
            }
          }, 300);
        }
        if (styleSheet.parentElement) {
          styleSheet.parentElement.removeChild(styleSheet);
        }
      } catch (e) {
        console.warn('[CameraService] Error closing camera modal:', e);
      }
    };

    captureBtn.addEventListener('click', async () => {
      console.log('[CameraService] Capturing photo from camera');
      try {
        // Add capture animation
        captureBtn.style.transform = 'scale(0.92)';
        setTimeout(() => {
          captureBtn.style.transform = 'scale(1)';
        }, 200);

        // Flash effect
        const flash = document.createElement('div');
        flash.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: white;
        z-index: 10000;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.1s ease;
      `;
        document.body.appendChild(flash);
        setTimeout(() => {
          flash.style.opacity = '0.8';
          setTimeout(() => {
            flash.style.opacity = '0';
            setTimeout(() => {
              if (flash.parentElement) {
                flash.parentElement.removeChild(flash);
              }
            }, 300);
          }, 100);
        }, 50);

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

        console.log('[CameraService] Photo captured from camera, processing...');
        closeModal();

        // Process like a normal photo
        const storageImage = await this.compressImage(dataUrl, this.STORAGE_COMPRESSION);
        const thumbnailImage = await this.compressImage(dataUrl, this.THUMBNAIL_COMPRESSION);
        const docId = await this.savePhotoToFirestore(storageImage, latitude, longitude);

        if (this._IDBphotoCacheService) {
          try {
            await this._IDBphotoCacheService.storeUrl(docId, dataUrl);
          } catch (cacheError) {
            console.warn('[CameraService] IDB cache error:', cacheError);
          }
        }

        console.log('[CameraService] Camera photo complete:', docId);
        resolve({
          photoUrl: dataUrl,
          thumbnailUrl: thumbnailImage,
          docId: docId,
          latitude: latitude,
          longitude: longitude
        });
      } catch (error) {
        console.error('[CameraService] Capture error:', error);
        closeModal();
        reject(error);
      }
    });

    cancelBtn.addEventListener('click', () => {
      console.log('[CameraService] User cancelled camera');
      closeModal();
      resolve(null);
    });

    // Hover effects for cancel button
    cancelBtn.addEventListener('mouseenter', () => {
      cancelBtn.style.background = 'rgba(255, 255, 255, 0.2)';
      cancelBtn.style.borderColor = 'rgba(255, 255, 255, 0.3)';
    });
    cancelBtn.addEventListener('mouseleave', () => {
      cancelBtn.style.background = 'rgba(255, 255, 255, 0.1)';
      cancelBtn.style.borderColor = 'rgba(255, 255, 255, 0.2)';
    });

    buttonsDiv.appendChild(captureBtn);
    buttonsDiv.appendChild(cancelBtn);
    containerDiv.appendChild(buttonsDiv);
    modalDiv.appendChild(containerDiv);
    document.body.appendChild(modalDiv);

    console.log('[CameraService] Camera preview modal opened');
  }


  private async processSelectedPhoto(
    fileInput: HTMLInputElement,
    latitude: number,
    longitude: number,
    resolve: (value: PhotoResult | null) => void,
    reject: (error: any) => void,
    cleanupInput: () => void
  ): Promise<void> {
    try {
      const file = fileInput.files?.[0];
      if (!file) {
        cleanupInput();
        resolve(null);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const originalDataUrl = e.target?.result as string;
          if (!originalDataUrl) {
            cleanupInput();
            resolve(null);
            return;
          }

          console.log('[CameraService] Processing selected photo');
          const storageImage = await this.compressImage(originalDataUrl, this.STORAGE_COMPRESSION);
          const thumbnailImage = await this.compressImage(originalDataUrl, this.THUMBNAIL_COMPRESSION);
          const docId = await this.savePhotoToFirestore(storageImage, latitude, longitude);

          if (this._IDBphotoCacheService) {
            try {
              await this._IDBphotoCacheService.storeUrl(docId, originalDataUrl);
            } catch (cacheError) {
              console.warn('[CameraService] IDB cache error:', cacheError);
            }
          }

          console.log('[CameraService] Photo processing complete:', docId);
          cleanupInput();
          resolve({
            photoUrl: originalDataUrl,
            thumbnailUrl: thumbnailImage,
            docId: docId,
            latitude: latitude,
            longitude: longitude
          });
        } catch (processError) {
          console.error('[CameraService] Processing error:', processError);
          cleanupInput();
          reject(processError);
        }
      };

      reader.onerror = () => {
        console.error('[CameraService] FileReader error');
        cleanupInput();
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('[CameraService] Photo processing error:', error);
      cleanupInput();
      reject(error);
    }
  }

  private capturePhotoFromCamera(latitude: number, longitude: number): Promise<PhotoResult | null> {
    return new Promise((resolve, reject) => {
      try {
        console.log('[CameraService] Requesting camera access via getUserMedia');

        navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1200 },
            height: { ideal: 1200 }
          }
        }).then((stream) => {
          console.log('[CameraService] Camera stream acquired');
          this.showCameraPreview(stream, latitude, longitude, resolve, reject);
        }).catch((error) => {
          console.error('[CameraService] Camera access denied:', error);
          if (error.name === 'NotAllowedError') {
            reject(new Error('Camera permission denied'));
          } else if (error.name === 'NotFoundError') {
            reject(new Error('No camera found on this device'));
          } else {
            reject(error);
          }
        });

      } catch (error) {
        console.error('[CameraService] Camera setup error:', error);
        reject(error);
      }
    });
  }


  private capturePhotoFromGallery(latitude: number, longitude: number): Promise<PhotoResult | null> {
    return new Promise((resolve, reject) => {
      try {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.id = 'gallery-input-' + Date.now();

        const cleanupInput = () => {
          try {
            if (fileInput.parentElement) {
              fileInput.parentElement.removeChild(fileInput);
            }
          } catch (e) {
            console.warn('[CameraService] Cleanup error:', e);
          }
        };

        const timeoutId = setTimeout(() => {
          console.log('[CameraService] Gallery picker timeout');
          cleanupInput();
          resolve(null);
        }, 60000);

        fileInput.addEventListener('change', async () => {
          console.log('[CameraService] Gallery file selected');
          clearTimeout(timeoutId);
          try {
            await this.processSelectedPhoto(fileInput, latitude, longitude, resolve, reject, cleanupInput);
          } catch (error) {
            reject(error);
          }
        });

        document.body.appendChild(fileInput);
        fileInput.click();
      } catch (error) {
        console.error('[CameraService] Gallery picker error:', error);
        reject(error);
      }
    });
  }

  private async savePhotoToFirestore(compressedDataUrl: string, latitude: number, longitude: number): Promise<string> {
    try {
      // Get current user ID
      let uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.log('[CameraService] No sync user ID available, fetching asynchronously');
        uid = this._anonymousUserService.currentUserId;
      }

      console.log('[CameraService] Uploading with user ID:', uid);

      // Try to upload with retry logic
      let attempt = 0;
      const maxRetries = 3;
      let lastError;

      while (attempt < maxRetries) {
        try {
          // Upload the pre-compressed image (no need to compress again)
          const downloadURL = await this._storageService.uploadUserPicture(uid, compressedDataUrl);

          // Save metadata to Firestore
          const docId = await this._firestoreService.savePhotoMetadata(
            downloadURL,
            latitude,
            longitude,
            uid,
            true
          );

          if (!docId) {
            console.error('[CameraService] No document ID returned from Firestore save');
            throw new Error('Failed to save metadata: No document ID returned');
          }

          console.log('[CameraService] Firestore save successful, docId:', docId);
          return docId;
        } catch (error) {
          lastError = error;
          console.error(`[CameraService] Upload attempt ${attempt + 1} failed:`, error);
          attempt++;

          if (attempt >= maxRetries) {
            console.error('[CameraService] Max retries reached, moving to fallback');
            throw error;
          }

          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`[CameraService] Retrying in ${delay}ms (${attempt}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      throw lastError;
    } catch (error) {
      console.error('[CameraService] Failed to upload image after multiple attempts:', error);

      // Fallback: try with even more compression if the original upload failed
      try {
        console.log('[CameraService] Attempting fallback with higher compression...');

        // Try with higher compression
        const highlyCompressedDataUrl = await this.compressImage(compressedDataUrl, this.FALLBACK_COMPRESSION);

        // Get user ID again (in case the first attempt failed due to ID issues)
        let uid = this._anonymousUserService.currentUserId;

        console.log('[CameraService] Uploading highly compressed image...');
        const downloadURL = await this._storageService.uploadUserPicture(uid, highlyCompressedDataUrl);
        console.log('[CameraService] Fallback upload successful');

        // Save metadata to Firestore using the FirestoreService
        console.log('[CameraService] Saving fallback to Firestore with:', { latitude, longitude, uid, compressed: true });
        const docId = await this._firestoreService.savePhotoMetadata(
          downloadURL,
          latitude,
          longitude,
          uid,
          true  // Indicate this is compressed
        );

        if (!docId) {
          console.error('[CameraService] No document ID returned from fallback Firestore save');
          throw new Error('Failed to save metadata in fallback: No document ID returned');
        }

        console.log('[CameraService] Fallback Firestore save successful, docId:', docId);
        return docId;
      } catch (fallbackError) {
        console.error('[CameraService] Fallback upload also failed:', fallbackError);
        throw new Error('Both standard and fallback upload attempts failed');
      }
    }
  }

  /**
   * Compresses an image to the specified dimensions and quality
   */
  compressImage(dataUrl: string, settings: CompressionSettings): Promise<string> {
    return new Promise((resolve, reject) => {
      // Create an image to load the data URL
      const img = new Image();

      img.onload = () => {
        // Create a canvas element to draw and compress the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Check if we got a valid context
        if (!ctx) {
          console.error('[CameraService] Failed to get 2d context for image compression');
          // Just return the original image if we can't compress
          resolve(dataUrl);
          return;
        }

        // Calculate new dimensions while preserving aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > settings.maxDimension) {
            height = Math.round((height * settings.maxDimension) / width);
            width = settings.maxDimension;
          }
        } else {
          if (height > settings.maxDimension) {
            width = Math.round((width * settings.maxDimension) / height);
            height = settings.maxDimension;
          }
        }

        // Set canvas size to the new dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw resized image on the canvas
        ctx.drawImage(img, 0, 0, width, height);

        try {
          // Preserve the original image format if possible
          // Extract the image format from the original dataUrl
          const formatMatch = dataUrl.match(/^data:image\/([a-zA-Z0-9]+);base64,/);
          const imageFormat = formatMatch ? formatMatch[1] : 'jpeg';

          // Use the same format as the original image with the specified quality
          const compressedDataUrl = canvas.toDataURL(`image/${imageFormat}`, settings.quality);

          // Calculate sizes
          const originalSize = dataUrl.length;
          const compressedSize = compressedDataUrl.length;

          // Log compression results
          console.log('[CameraService] Image compression:', {
            originalSize: Math.round(originalSize / 1024), // KB
            compressedSize: Math.round(compressedSize / 1024), // KB
            originalDimensions: `${img.width}x${img.height}`,
            newDimensions: `${width}x${height}`,
            quality: settings.quality
          });

          // Only use the compressed version if it's actually smaller
          // This prevents the issue where compression increases file size
          if (compressedSize < originalSize) {
            console.log('[CameraService] Using compressed image (saved ' +
              Math.round((originalSize - compressedSize) / 1024) + ' KB)');
            resolve(compressedDataUrl);
          } else {
            console.log('[CameraService] Compression ineffective, using original image');
            resolve(dataUrl);
          }
        } catch (error) {
          console.error('[CameraService] Error during canvas toDataURL:', error);
          // Return original if compression fails
          resolve(dataUrl);
        }
      };

      img.onerror = () => {
        console.error('[CameraService] Failed to load image for compression');
        // Return original if we can't load it
        resolve(dataUrl);
      };

      // Load the image
      img.src = dataUrl;
    });
  }
}